import { css } from '@styled-system/css';
import { token } from '@styled-system/tokens';
import type { SystemProperties } from '@styled-system/types';

import { DownloadIcon } from '@/shared/components/ui/Logo/DownloadIcon';

import { AccountIcon } from './AccountIcon';
import { AddIcon } from './AddIcon';
import { ArrowIcon } from './ArrowIcon';
import { BackIcon } from './BackIcon';
import { BasketIcon } from './BasketIcon';
import { BlitzIcon } from './BlitzIcon';
import { CloseIcon } from './CloseIcon';
import { CrownIcon } from './CrownIcon';
import { CubeIcon } from './CubeIcon';
import { DiscordIcon } from './DiscordIcon';
import { ErrorIcon } from './ErrorIcon';
import { ExternalLinkIcon } from './ExternalLinkIcon';
import { GlobeIcon } from './GlobeIcon';
import { InfoIcon } from './InfoIcon';
import { LogoutIcon } from './LogoutIcon';
import { MessageIcon } from './MessageIcon';
import { ParamIcon } from './ParamIcon';
import { PaymentIcon } from './PaymentIcon';
import { PlusIcon } from './PlusIcon';
import { RenameIcon } from './RenameIcon';
import { StarIcon } from './StarIcon';
import { StopIcon } from './StopIcon';
import { TagIcon } from './TagIcon';
import { TrashIcon } from './TrashIcon';

export const logoVariants = [
  'account',
  'plus',
  'trash',
  'rename',
  'stop',
  'arrow',
  'back',
  'add',
  'info',
  'blitz',
  'error',
  'close',
  'crown',
  'param',
  'logout',
  'tag',
  'basket',
  'cube',
  'globe',
  'star',
  'message',
  'discord',
  'external-link',
  'payment',
  'download',
] as const;
export type LogoVariant = (typeof logoVariants)[number];

interface LogoProps {
  variant: LogoVariant;
  className?: string;
  color?: SystemProperties['color'];
}

const iconComponents = {
  account: AccountIcon,
  plus: PlusIcon,
  trash: TrashIcon,
  rename: RenameIcon,
  stop: StopIcon,
  arrow: ArrowIcon,
  back: BackIcon,
  add: AddIcon,
  info: InfoIcon,
  blitz: BlitzIcon,
  error: ErrorIcon,
  close: CloseIcon,
  crown: CrownIcon,
  param: ParamIcon,
  logout: LogoutIcon,
  tag: TagIcon,
  basket: BasketIcon,
  cube: CubeIcon,
  globe: GlobeIcon,
  star: StarIcon,
  message: MessageIcon,
  discord: DiscordIcon,
  'external-link': ExternalLinkIcon,
  payment: PaymentIcon,
  download: DownloadIcon,
} as const;

export const Logo = ({ variant, className, color }: LogoProps) => {
  const IconComponent = iconComponents[variant];

  if (!IconComponent) {
    return null;
  }

  // Résoudre la couleur du token Panda CSS en valeur CSS réelle
  const resolvedColor = color
    ? typeof color === 'string' && color.includes('.')
      ? token.var(`colors.${color}` as any)
      : color
    : undefined;

  const logoStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    w: '0.625rem',
    h: '0.625rem',
    flexShrink: 0,
  });

  return (
    <div className={`${logoStyles} ${className || ''}`}>
      <IconComponent color={typeof resolvedColor === 'string' ? resolvedColor : undefined} />
    </div>
  );
};

// Re-export all icon components and types
export {
  AccountIcon,
  PlusIcon,
  TrashIcon,
  RenameIcon,
  StopIcon,
  ArrowIcon,
  BackIcon,
  AddIcon,
  InfoIcon,
  BlitzIcon,
  ErrorIcon,
  CloseIcon,
  CrownIcon,
  ParamIcon,
  LogoutIcon,
  TagIcon,
  BasketIcon,
  CubeIcon,
  GlobeIcon,
  StarIcon,
  MessageIcon,
  DiscordIcon,
  ExternalLinkIcon,
  PaymentIcon,
};
export type { LogoProps };
